import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { SubmitButton } from '@/components/ui/Button';

interface ImplementationStartCardProps {
  // Current implementation start date in YYYY-MM-DD, or null if not started.
  startedAt: string | null;
  isReadOnly: boolean;
  isSaving: boolean;
  // Pass the chosen date (YYYY-MM-DD) to set/update, or null to reset.
  onSubmit: (date: string | null) => void;
}

const formatLongDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
};

const todayISO = (): string => new Date().toISOString().slice(0, 10);

export const ImplementationStartCard = ({
  startedAt,
  isReadOnly,
  isSaving,
  onSubmit,
}: ImplementationStartCardProps) => {
  const [dateInput, setDateInput] = useState<string>(startedAt ?? todayISO());

  // Keep the input in sync when the server value changes.
  useEffect(() => {
    if (startedAt) {
      setDateInput(startedAt);
    } else {
      setDateInput(todayISO());
    }
  }, [startedAt]);

  const isRunning = Boolean(startedAt);

  const handleReset = () => {
    setDateInput(todayISO());
    onSubmit(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
            <Calendar size={18} />
          </span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Tanggal Mulai Implementasi</span>
          <input
            type="date"
            value={dateInput}
            onChange={e => setDateInput(e.target.value)}
            disabled={isReadOnly || isSaving}
            className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500"
            aria-label="Tanggal mulai implementasi"
          />
          {!isReadOnly && (
            <SubmitButton
              type="button"
              onClick={() => onSubmit(dateInput)}
              disabled={isSaving || !dateInput || dateInput === startedAt}
            >
              {isSaving ? 'Menyimpan...' : 'Mulai'}
            </SubmitButton>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm">
          {isRunning ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 dark:bg-orange-900/30 px-3 py-1 font-medium text-orange-600 dark:text-orange-400">
                <span className="h-2 w-2 rounded-full bg-orange-500 dark:bg-orange-400" />
                Berjalan
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                Dimulai sejak {formatLongDate(startedAt as string)}.
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 font-medium text-gray-500 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-gray-400 dark:bg-gray-500" />
              Belum dimulai
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
